import React from 'react';

export enum AVideoType {
  Brightcove = 'Brightcove',
  Html5 = 'Html5',
}

export enum AVideoAspectRatio {
  Ratio1x1 = '1:1',
  Ratio3x2 = '3:2',
  Ratio3x1 = '3:1',
  Ratio16x9 = '16:9',
  Ratio5x2 = '5:2',
  Ratio2x3 = '2:3',
}

/** @brightcove Player ref returned by Brightcove's onSuccess callback */
export interface BrightcovePlayerRef {
  play?: () => Promise<void>;
  pause?: () => void;
  controls?: (show: boolean) => void;
  on?: (event: string, callback: () => void) => void;
  off?: (event: string, callback: () => void) => void;
  /** @brightcove Standard HTML media readyState (0-4); >= 3 (HAVE_FUTURE_DATA) means playback can start. */
  readyState?: () => number;
  el_?: HTMLElement;
}

/** @brightcove Wrapper for Brightcove's onSuccess payload */
export interface BrightcovePlayerSuccess {
  ref: BrightcovePlayerRef;
}

/**
 * Base props shared by all player implementations.
 * Extends HTMLDivElement attributes per mi-web-ui-kit atom convention
 * (root element is a wrapper div).
 */
export interface AVideoBaseProps extends React.HTMLAttributes<HTMLDivElement> {
  aspectRatio?: AVideoAspectRatio;
  isPlaying?: boolean;
  autoPlay?: boolean;
  muted?: boolean;
  loop?: boolean;
  controls?: boolean;
  /**
   * Force-show the closed-captions button in the control bar even when no
   * text tracks are detected at load time. Brightcove only.
   *
   * The button becomes visible but non-functional until tracks are available
   * in the Brightcove Studio account. Wired in a future PR.
   */
  captions?: boolean;
  /**
   * Force-show the audio-description button in the control bar even when no
   * audio-description tracks are detected at load time. Brightcove only.
   *
   * The button becomes visible but non-functional until tracks are available
   * in the Brightcove Studio account. Wired in a future PR.
   */
  audioDescription?: boolean;
  onAutoplayBlocked?: () => void;
  onEnded?: () => void;
  /**
   * Fires when the player's loading state changes.
   * `true` on load start (mount with valid source), `false` when media is
   * ready to play or on error.
   *
   * When provided, the atom CSS-hides the player during loading so the consumer
   * can render their own skeleton. When absent, current behavior is preserved.
   *
   * Designed as the bridge point for future Suspense helpers.
   */
  onLoadingChange?: (loading: boolean) => void;
}

/** @brightcove Props specific to the Brightcove player integration. */
export interface AVideoBrightcoveProps {
  titleBar?: boolean;
  videoId: string;
  videoAccountId: string;
  playerId?: string;
  onPlayerReady?: (player: BrightcovePlayerRef) => void;
  onPlayerError?: (error?: unknown) => void;
}

export interface AVideoHtml5Props {
  src: string;
  poster?: string;
  title?: string;
  playsInline?: boolean;
  onPlayerError?: (error?: unknown) => void;
}

/**
 * Public props for the AVideo component.
 * Discriminated union on videoType -- extend when adding new player types.
 */
export type AVideoProps =
  | ({ videoType: AVideoType.Brightcove } & AVideoBaseProps & AVideoBrightcoveProps)
  | ({ videoType?: AVideoType.Html5 } & AVideoBaseProps & AVideoHtml5Props);

/**
 * Internal props contract for the Brightcove player sub-component.
 * videoType is consumed by the registry router and stripped before passing to the player.
 */
export type AVideoBrightcoveInternalProps = AVideoBaseProps & AVideoBrightcoveProps;

export type AVideoHtml5InternalProps = AVideoBaseProps & AVideoHtml5Props;
